export const LifesHubColors = {
    green: '#26e51f',
    green700: '#0c8f14',
    green800: '#08720f',
    green100: '#e9fbe9',
    green050: '#f4fff4',

    ink: '#171a1c',
    muted: '#66706a',
    muted2: '#8b948f',

    topbar: '#060707',
    sidebar: '#202324',
    sidebar2: '#17191a',

    surface: '#ffffff',
    surface2: '#f7f9f8',
    canvas: '#f1f4f2',
    border: '#dfe5e1',
    borderStrong: '#cbd4ce',

    danger: '#c62828',
    dangerBg: '#fff0f0',
    warning: '#b86600',
    warningBg: '#fff7e8',
    blue: '#2563eb',
    blueBg: '#eff6ff',
    purple: '#69418f',

    gridLine: '#eef1ef',

    chartBase: '#82d1a2',
    chartEmphasis: '#6fbf8e',
    chartTrack: '#eef8f2',
    greenGlow: 'rgba(38, 229, 31, .48)'
}

export const LifesHubShadows = {
    shadow1: '0 1px 2px rgba(10, 20, 13, .05), 0 10px 30px rgba(15, 25, 18, .05)',
    shadow2: '0 20px 70px rgba(0, 0, 0, .24)'
}

export const LifesHubRadius = {
    card: '7px',
    panel: '11px',
    banner: '10px',
    control: '9px',
    page: '8px',
    pill: '999px'
}

export const LIFESHUB_ROOT_CSS = `:root {
    --green: ${LifesHubColors.green};
    --green-700: ${LifesHubColors.green700};
    --green-800: ${LifesHubColors.green800};
    --green-100: ${LifesHubColors.green100};
    --green-050: ${LifesHubColors.green050};

    --ink: ${LifesHubColors.ink};
    --muted: ${LifesHubColors.muted};
    --muted-2: ${LifesHubColors.muted2};

    --topbar: ${LifesHubColors.topbar};
    --sidebar: ${LifesHubColors.sidebar};
    --sidebar-2: ${LifesHubColors.sidebar2};

    --surface: ${LifesHubColors.surface};
    --surface-2: ${LifesHubColors.surface2};
    --canvas: ${LifesHubColors.canvas};
    --border: ${LifesHubColors.border};
    --border-strong: ${LifesHubColors.borderStrong};

    --danger: ${LifesHubColors.danger};
    --danger-bg: ${LifesHubColors.dangerBg};
    --warning: ${LifesHubColors.warning};
    --warning-bg: ${LifesHubColors.warningBg};
    --blue: ${LifesHubColors.blue};
    --blue-bg: ${LifesHubColors.blueBg};

    --chart-base: ${LifesHubColors.chartBase};
    --chart-emphasis: ${LifesHubColors.chartEmphasis};
    --chart-track: ${LifesHubColors.chartTrack};

    --shadow-1: ${LifesHubShadows.shadow1};
    --shadow-2: ${LifesHubShadows.shadow2};

    --radius: ${LifesHubRadius.card};
    --radius-sm: ${LifesHubRadius.control};
    --header-h: 68px;
    --sidebar-w: 252px;

    --font: Inter, ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}`

export const chartValueLabelStyle = {
    color: LifesHubColors.ink
}

export type LifesHubChipTone = 'green' | 'blue' | 'orange' | 'red' | 'gray' | 'purple' | 'teal'

const CHIP_TONES: Record<LifesHubChipTone, { color: string; background: string; borderColor: string }> = {
    green: { color: '#176a1d', background: '#e7f7e8', borderColor: '#c8ebca' },
    blue: { color: '#285797', background: '#edf4ff', borderColor: '#d5e4fa' },
    orange: { color: '#a25200', background: '#fff1e3', borderColor: '#ffd8b4' },
    red: { color: '#b52222', background: '#fff0f0', borderColor: '#ffd3d3' },
    gray: { color: '#555e59', background: '#f0f2f1', borderColor: '#e2e5e3' },
    purple: { color: '#69418f', background: '#f5edfc', borderColor: '#eadcf8' },
    teal: { color: '#15665f', background: '#e9f7f5', borderColor: '#d0ece8' }
}

export function chipSx(tone: LifesHubChipTone) {
    const palette = CHIP_TONES[tone]

    return {
        height: 25,
        borderRadius: LifesHubRadius.pill,
        border: `1px solid ${palette.borderColor}`,
        backgroundColor: palette.background,
        color: palette.color,
        fontSize: 11.5,
        fontWeight: 700,
        '& .MuiChip-label': { padding: '0 9px' }
    }
}

export type LifesHubButtonVariant = 'default' | 'primary' | 'brand' | 'tertiary' | 'danger'

export function buttonSx(variant: LifesHubButtonVariant = 'default', compact = false) {
    const base = {
        minHeight: compact ? 34 : 40,
        padding: compact ? '6px 11px' : '9px 16px',
        gap: '9px',
        borderRadius: LifesHubRadius.control,
        border: `1px solid ${LifesHubColors.borderStrong}`,
        backgroundColor: '#fff',
        color: LifesHubColors.ink,
        fontSize: compact ? 12 : 14,
        fontWeight: 650,
        textTransform: 'none' as const,
        boxShadow: '0 1px 1px rgba(0,0,0,.02)',
        lineHeight: 1.2,
        whiteSpace: 'nowrap' as const,
        transition: 'transform .12s ease, border-color .15s ease, background .15s ease, box-shadow .15s ease',
        '& .MuiButton-startIcon': { marginRight: 0, marginLeft: 0 },
        '& .MuiSvgIcon-root': { fontSize: compact ? 16 : 20 },
        '&:hover': {
            transform: 'translateY(-1px)',
            borderColor: '#aebbb3',
            backgroundColor: '#fff',
            boxShadow: '0 5px 16px rgba(0,0,0,.06)'
        },
        '&.Mui-disabled': {
            color: '#9aa39d',
            borderColor: LifesHubColors.border,
            backgroundColor: '#f7f8f7',
            boxShadow: 'none'
        }
    }

    switch (variant) {
        case 'primary':
            return {
                ...base,
                color: '#fff',
                borderColor: '#093e0b',
                background: 'linear-gradient(180deg, #0e6412, #08440b)',
                boxShadow: '0 6px 16px rgba(9,85,14,.2)',
                '&:hover': {
                    ...base['&:hover'],
                    borderColor: '#093e0b',
                    background: 'linear-gradient(180deg, #117816, #0b5410)'
                }
            }
        case 'brand':
            return {
                ...base,
                color: LifesHubColors.green,
                borderColor: '#174c19',
                backgroundColor: '#0a0d0b',
                '&:hover': { ...base['&:hover'], borderColor: LifesHubColors.green, backgroundColor: '#0a0d0b' }
            }
        case 'tertiary':
            return {
                ...base,
                color: LifesHubColors.green800,
                borderColor: 'transparent',
                backgroundColor: 'transparent',
                boxShadow: 'none',
                '&:hover': {
                    ...base['&:hover'],
                    borderColor: 'transparent',
                    backgroundColor: LifesHubColors.green050,
                    boxShadow: 'none'
                }
            }
        case 'danger':
            return {
                ...base,
                color: LifesHubColors.danger,
                borderColor: '#efb8b8',
                '&:hover': { ...base['&:hover'], borderColor: '#d98b8b', backgroundColor: LifesHubColors.dangerBg }
            }
        default:
            return base
    }
}

export type LifesHubBannerTone = 'green' | 'blue' | 'warning' | 'danger'

export function bannerSx(tone: LifesHubBannerTone) {
    const palette = {
        green: { color: '#245a27', borderColor: '#cfe8d1', background: LifesHubColors.green050 },
        blue: { color: '#1e4e91', borderColor: '#cddff8', background: LifesHubColors.blueBg },
        warning: { color: '#6d4605', borderColor: '#f3d6a3', background: LifesHubColors.warningBg },
        danger: { color: '#8f2020', borderColor: '#efb8b8', background: LifesHubColors.dangerBg }
    }[tone]

    return {
        alignItems: 'flex-start',
        gap: '11px',
        padding: '12px 14px',
        borderRadius: LifesHubRadius.banner,
        border: `1px solid ${palette.borderColor}`,
        backgroundColor: palette.background,
        color: palette.color,
        fontSize: 13,
        '& .MuiAlert-icon': { color: palette.color, padding: 0, marginRight: 0, opacity: 1 },
        '& .MuiAlert-message': { padding: 0 }
    }
}

export const inputSx = {
    '& .MuiOutlinedInput-root': {
        minHeight: 43,
        borderRadius: LifesHubRadius.control,
        backgroundColor: '#fff',
        fontSize: 14,
        '& fieldset': { borderColor: LifesHubColors.borderStrong },
        '&:hover fieldset': { borderColor: '#aebbb3' },
        '&.Mui-focused fieldset': { borderColor: LifesHubColors.green700, borderWidth: 1 },
        '&.Mui-focused': { boxShadow: 'rgba(38,229,31,.12) 0 0 0 3px' },
        '&.Mui-error fieldset': { borderColor: '#d98b8b' },
        '&.Mui-error.Mui-focused': { boxShadow: 'rgba(198,40,40,.10) 0 0 0 3px' }
    },
    '& .MuiOutlinedInput-input': { padding: '11px 12px' },
    '& .MuiFormHelperText-root': {
        marginLeft: 0,
        marginTop: '6px',
        fontSize: 11.5,
        color: LifesHubColors.muted
    },
    '& .MuiFormHelperText-root.Mui-error': { color: LifesHubColors.danger }
}

export const switchSx = {
    '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
        backgroundColor: LifesHubColors.green700,
        opacity: 1
    },
    '& .MuiSwitch-track': { backgroundColor: '#b8c0bb', opacity: 1 }
}

export const cardSx = {
    backgroundColor: LifesHubColors.surface,
    border: `1px solid ${LifesHubColors.border}`,
    borderRadius: LifesHubRadius.card,
    boxShadow: LifesHubShadows.shadow1,
    backgroundImage: 'none'
}

export const dataGridSx = {
    flexGrow: 1,
    minHeight: 0,
    border: 'none',
    fontSize: 12,
    color: LifesHubColors.ink,
    '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': {
        height: '0.8em'
    },
    '& .MuiDataGrid-columnHeaders': {
        backgroundColor: '#fbfcfb',
        borderBottom: '1px solid var(--border)',
        borderRadius: 0
    },
    '& .MuiDataGrid-columnHeaderTitle': {
        fontSize: 11.5,
        fontWeight: 700,
        color: '#2a302c'
    },
    '& .MuiDataGrid-cell': {
        borderBottom: '1px solid var(--border)'
    },
    '& .MuiDataGrid-footerContainer': {
        borderTop: '1px solid var(--border)',
        color: LifesHubColors.muted
    },
    '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
        outline: 'none'
    },
    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
        outline: 'none'
    }
}

export const dataGridRowHoverSx = {
    '& .MuiDataGrid-row:hover': {
        background: 'linear-gradient(90deg, var(--green-050), #fbfffb)'
    }
}

// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
// :: Menu lateral
// ::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

/** Texto e ícone do item inativo do menu. */
export const SIDEBAR_INK = '#e3e7e4'

/** Moldura escura do menu: degradê `--sidebar` → `--sidebar-2`. */
export const sidebarSurfaceSx = {
    backgroundColor: LifesHubColors.sidebar,
    backgroundImage: `linear-gradient(180deg, ${LifesHubColors.sidebar}, ${LifesHubColors.sidebar2})`,
    borderRight: '1px solid rgba(255, 255, 255, .04)'
}

/** Lista de navegação: transparente, para o degradê da moldura aparecer. */
export const sidebarNavSx = {
    backgroundColor: 'transparent',
    color: SIDEBAR_INK
}

/** Divisória entre grupos do menu. */
export const sidebarDividerSx = {
    borderColor: 'rgba(255, 255, 255, .1)'
}

/** Ícone do item de menu. */
export const sidebarNavIconSx = {
    color: SIDEBAR_INK,
    fill: SIDEBAR_INK
}

/**
 * Item de menu: repouso, hover e ativo. O estado ativo é lido de `data-active`,
 * que a view já emite — nenhuma marcação nova é introduzida aqui.
 *
 * A página aberta é marcada só por texto/ícone em `--green` e pela barra verde
 * rente à borda esquerda da coluna; o item não ganha fundo próprio. O `left: -10`
 * da barra cancela a margem lateral do item para ela encostar na borda, sem que a
 * margem em si mude.
 */
export const sidebarNavItemSx = {
    color: SIDEBAR_INK,
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, .055) !important',
        color: '#fff'
    },
    '&:hover svg': {
        color: '#fff',
        fill: '#fff'
    },
    '&[data-active="true"]': {
        color: `${LifesHubColors.green} !important`
    },
    '&[data-active="true"] svg': {
        color: `${LifesHubColors.green} !important`,
        fill: `${LifesHubColors.green} !important`
    },
    '&[data-active="true"]::before': {
        content: '""',
        position: 'absolute' as const,
        top: 7,
        bottom: 7,
        left: -10,
        width: 5,
        borderRadius: '0 5px 5px 0',
        backgroundColor: LifesHubColors.green,
        boxShadow: `0 0 14px ${LifesHubColors.greenGlow}`
    }
}
